import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { shopifyApi } from '../api/shopifyClient';
import { HttpError } from '../api/client';

/**
 * App Bridge Context
 * Provides access to the Shopify App Bridge instance loaded via CDN
 * All child components can use the useAppBridge() hook to access App Bridge
 */

// Minimal type definition for Shopify App Bridge global
// Full types are provided by @shopify/app-bridge-types package
// Note: The new App Bridge (App Home) does NOT have a redirect API.
// Use window.open(url, '_top') to break out of the iframe instead.
interface ShopifyGlobalType {
  idToken: () => Promise<string>;
  toast: {
    show: (options: { message: string; duration?: number; isError?: boolean }) => void;
  };
  // Add other methods as needed
}

interface ShopifyAppBridgeContextValue {
  shopify: ShopifyGlobalType | null;
  isReady: boolean;
  appBridgeError: string | null;
  /**
   * When the backend returns `connectionActive: false` from app-bridge-config,
   * the frontend must call `activateConnection` with a session token to
   * complete activation via Token Exchange before the app can function.
   */
  connectionActive: boolean | null;
}

const ShopifyAppBridgeContext = createContext<ShopifyAppBridgeContextValue>({
  shopify: null,
  isReady: false,
  appBridgeError: null,
  connectionActive: null,
});

interface ShopifyAppBridgeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes App Bridge available to all child components
 * Waits for the CDN script to load and initializes the global shopify object
 */
const APP_BRIDGE_CDN_URL = 'https://cdn.shopify.com/shopifycloud/app-bridge.js';

/**
 * How long to wait for window.shopify to appear before surfacing an error (ms).
 * Set generously: on a hard refresh of an embedded admin app, the parent frame
 * sometimes takes 15-20s to inject window.shopify into the iframe (Shopify's
 * own SPA rehydrates after the iframe loads). Showing a hard error during
 * that window makes the app look broken on reload.
 */
const APP_BRIDGE_TIMEOUT_MS = 30_000;

/** Polling interval while waiting for the global (ms) */
const APP_BRIDGE_POLL_INTERVAL_MS = 200;

function getShopFromUrl(): string | null {
  const rawShop = new URLSearchParams(window.location.search).get('shop');
  if (!rawShop) return null;

  const normalized = rawShop.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function ensureApiKeyMetaTag(apiKey: string): void {
  let metaTag = document.querySelector<HTMLMetaElement>('meta[name="shopify-api-key"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = 'shopify-api-key';
    document.head.appendChild(metaTag);
  }

  metaTag.content = apiKey;
}

async function resolveRuntimeApiKey(shop: string | null): Promise<{ apiKey: string; connectionActive: boolean } | null> {
  try {
    const config = await shopifyApi.getAppBridgeConfig(shop ?? undefined);
    if (!config.apiKey) return null;
    return {
      apiKey: config.apiKey,
      connectionActive: config.connectionActive !== false, // default true for backward compat
    };
  } catch (error) {
    // If the backend returns 404 "Shop not connected", it means this shop has
    // no ShopConnection at all (not even an inactive one). There is nothing
    // the frontend can do — the operator must create a connection first.
    if (error instanceof HttpError && error.status === 404) {
      return null;
    }
    return null;
  }
}

async function ensureAppBridgeScript(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${APP_BRIDGE_CDN_URL}"]`);

  if (existing) {
    // If a matching script tag already exists, do not await a new load event.
    // In SPA navigation, the script may have already fired 'load', and adding
    // listeners afterward would never resolve. Bootstrap readiness is enforced
    // by waitForAppBridgeGlobal() polling.
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APP_BRIDGE_CDN_URL;
    script.async = false;
    script.defer = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Shopify App Bridge script'));

    const firstScript = document.head.querySelector('script');
    if (firstScript) {
      document.head.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

async function waitForAppBridgeGlobal(signal?: AbortSignal): Promise<void> {
  // Fast path: already available
  if (window.shopify?.idToken) return;

  const start = Date.now();

  await new Promise<void>((resolve, reject) => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let visibilityHandler: (() => void) | null = null;

    const cleanup = () => {
      if (intervalId !== null) clearInterval(intervalId);
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal?.addEventListener('abort', onAbort);

    const check = () => {
      if (signal?.aborted) return; // onAbort will reject

      if (window.shopify?.idToken) {
        cleanup();
        resolve();
        return;
      }

      if (Date.now() - start >= APP_BRIDGE_TIMEOUT_MS) {
        cleanup();
        reject(new Error(
          'Shopify App Bridge did not load within the expected time. ' +
          'Please ensure this page is opened inside the Shopify admin.'
        ));
        return;
      }
    };

    // Re-check immediately when the tab becomes visible again — Shopify's admin
    // sometimes only injects window.shopify after the iframe is actually visible
    // (e.g. after navigating back from another tab). This avoids waiting out the
    // full poll interval on the user's "navigate away and back" recovery path.
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    intervalId = setInterval(check, APP_BRIDGE_POLL_INTERVAL_MS);
  });
}

export function ShopifyAppBridgeProvider({ children }: ShopifyAppBridgeProviderProps) {
  const [shopify, setShopify] = useState<ShopifyGlobalType | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [appBridgeError, setAppBridgeError] = useState<string | null>(null);
  const [connectionActive, setConnectionActive] = useState<boolean | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const shop = getShopFromUrl();
        const resolved = await resolveRuntimeApiKey(shop);

        if (!resolved) {
          throw new Error('Shopify App Bridge API key is not configured.');
        }

        ensureApiKeyMetaTag(resolved.apiKey);
        await ensureAppBridgeScript();
        await waitForAppBridgeGlobal(abortController.signal);

        if (cancelled || abortController.signal.aborted) return;

        setShopify(window.shopify as ShopifyGlobalType);
        setIsReady(true);
        setConnectionActive(resolved.connectionActive);
        setAppBridgeError(null);
      } catch (error) {
        if (cancelled || abortController.signal.aborted) return;
        // AbortError is expected on unmount; don't surface it as a user-visible error.
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setIsReady(false);
        setAppBridgeError(error instanceof Error ? error.message : 'Shopify App Bridge failed to initialize');
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, []);

  const value = useMemo(() => ({ shopify, isReady, appBridgeError, connectionActive }), [shopify, isReady, appBridgeError, connectionActive]);

  return (
    <ShopifyAppBridgeContext.Provider value={value}>
      {children}
    </ShopifyAppBridgeContext.Provider>
  );
}

/**
 * Hook to access App Bridge in functional components
 * Returns the shopify global object and ready state
 */
export function useAppBridge() {
  const context = useContext(ShopifyAppBridgeContext);
  if (!context) {
    throw new Error('useAppBridge must be used within ShopifyAppBridgeProvider');
  }
  return context;
}
